import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Copy, Check } from 'lucide-react';
import { useMFA } from '@/hooks/useMFA';
import { useAuth } from '@/modules/auth/hooks/useAuth';

interface TOTPEnrollmentProps {
  onEnrollmentComplete: () => void;
  onCancel?: () => void;
  required?: boolean;
}

export function TOTPEnrollment({ onEnrollmentComplete, onCancel, required = false }: TOTPEnrollmentProps) {
  const { t } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const {
    enrollmentData,
    loading,
    error,
    enrollMFA,
    verifyMFA,
    checkMFAStatus,
    cleanupUnverifiedFactors
  } = useMFA();
  const [verificationCode, setVerificationCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [step, setStep] = useState<'enroll' | 'verify' | 'cleanup'>('enroll');
  const [showCleanupOption, setShowCleanupOption] = useState(false);

  // Remove auto-check on mount to prevent connection issues

  const handleEnroll = async () => {
    // First check if already enrolled
    const status = await checkMFAStatus();
    if (status.enrolled && status.verified) {
      // Route to verification instead
      if (onCancel) {
        onCancel();
      }
      return;
    }

    // If there's an unverified factor, show the cleanup option. Read the FRESH
    // status.hasUnverified returned by checkMFAStatus above — NOT the hook's
    // hasUnverifiedFactor state, which is one render tick stale here (the setState
    // inside checkMFAStatus hasn't re-rendered this component yet), so the prompt
    // would never appear on the first click.
    if (status.hasUnverified) {
      setShowCleanupOption(true);
      return;
    }

    const result = await enrollMFA();
    if (result) {
      setStep('verify');
    }
  };

  const handleCleanupAndRestart = async () => {
    setStep('cleanup');
    const cleaned = await cleanupUnverifiedFactors();
    if (cleaned) {
      setShowCleanupOption(false);
      const result = await enrollMFA(true);
      if (result) {
        setStep('verify');
      } else {
        setStep('enroll');
      }
    } else {
      setStep('enroll');
    }
  };

  const handleVerify = async () => {
    if (!verificationCode) return;

    const success = await verifyMFA(verificationCode);
    if (success) {
      onEnrollmentComplete();
    }
  };

  const copySecret = async () => {
    if (enrollmentData?.totp?.secret) {
      await navigator.clipboard.writeText(enrollmentData.totp.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  if (loading && (step === 'enroll' || step === 'cleanup')) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center space-x-2">
            <Shield className="h-5 w-5 animate-pulse" />
            <span>{step === 'cleanup' ? t('settings:totp.cleaningUp') : t('settings:totp.settingUp')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center space-x-2">
          <Shield className="h-5 w-5" />
          <span>{t('settings:totp.setupTitle')}</span>
        </CardTitle>
        <CardDescription>
          {required
            ? t('settings:totp.requiredDescription')
            : t('settings:totp.optionalDescription')
          }
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              {error}
              {error.includes('already exists') && (
                <div className="mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCleanupAndRestart}
                    className="h-8"
                  >
                    {t('settings:totp.cleanUpAndRestart')}
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {showCleanupOption && (
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              {t('settings:totp.incompleteSetupFound')}
              <div className="mt-3 flex space-x-2">
                <Button size="sm" onClick={handleCleanupAndRestart}>
                  {t('settings:totp.cleanUpAndRestart')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowCleanupOption(false)}>
                  {t('common:cancel')}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {step === 'verify' && enrollmentData && (
          <>
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                {(() => {
                  try {
                    // Create a minimal otpauth URI with only essential parameters
                    const secret = enrollmentData.totp.secret;
                    const friendlyName = enrollmentData.friendly_name || user?.email || 'User';
                    const issuer = 'OkosTec';

                    // Build minimal otpauth URI
                    const otpauthUri = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(friendlyName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;

                    console.log('Generated otpauth URI length:', otpauthUri.length);

                    // Check if the minimal URI is still too long (should be much shorter now)
                    if (otpauthUri.length > 2953) {
                      throw new Error('Even minimal QR code data is too long');
                    }

                    return (
                      <QRCodeSVG
                        value={otpauthUri}
                        size={200}
                        className="border border-border rounded-lg p-2"
                      />
                    );
                  } catch (error) {
                    console.error('QR code generation error:', error);
                    return (
                      <div className="border border-border rounded-lg p-8 text-center">
                        <Shield className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {t('settings:totp.qrUnavailable')}
                        </p>
                      </div>
                    );
                  }
                })()}
              </div>

              <div className="text-sm space-y-2">
                <p className="font-medium">{t('settings:totp.scanQrCode')}</p>
                <p className="text-muted-foreground">
                  {t('settings:totp.manualEntryHint')}
                </p>
                <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
                  <code className="flex-1 text-xs font-mono">
                    {enrollmentData.totp.secret}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copySecret}
                    className="h-8 w-8 p-0"
                  >
                    {secretCopied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium">
                {t('settings:totp.enterCodeLabel')}
              </label>
              <Input
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyPress={handleKeyPress}
                className="text-center text-lg tracking-widest font-mono"
                maxLength={6}
              />
            </div>

            <div className="flex space-x-2">
              <Button
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1"
              >
                {loading ? t('settings:totp.verifying') : t('settings:totp.verifyAndComplete')}
              </Button>
              {!required && onCancel && (
                <Button variant="outline" onClick={onCancel}>
                  {t('common:cancel')}
                </Button>
              )}
            </div>
          </>
        )}

        {step === 'enroll' && !loading && !showCleanupOption && (
          <div className="text-center">
            <Button onClick={handleEnroll} disabled={loading}>
              {t('settings:totp.startSetup')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
